import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";
import SearchInput from "components/inputs/SearchInput/SearchInput";
import { Checkbox } from "components/table/components/Checkbox/Checkbox";
import { ActiveLabel } from "components/table/components/ActiveLabel/ActiveLabel";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import { SegmentItem } from "store/segments/slice";

import styles from "./PushSendTab.module.scss";

type Props = {
  selectedSegmentIds: number[];
  onSelectionChange: (ids: number[]) => void;
  onLoadingChange?: (isLoading: boolean) => void;
  segmentType?: "manual" | "auto";
};

const isManualSegmentEligible = (segment: SegmentItem) =>
  Boolean(segment.isActive) && (segment.countOfUsers ?? 0) > 0;

export const PushSendTab: React.FC<Props> = ({
  selectedSegmentIds,
  onSelectionChange,
  onLoadingChange,
  segmentType = "manual",
}) => {
  const showUsersColumn = segmentType === "manual";
  const { t } = useTranslation();
  const [segments, setSegments] = useState<SegmentItem[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadSegments = async () => {
      setIsLoading(true);
      onLoadingChange?.(true);

      try {
        const result = await instance.post(apiRoutes.segmentsList, {
          limit: 1000,
          offset: 0,
          order: {
            title: "asc",
            isActive: "desc",
          },
          type: segmentType,
        });

        if (isMounted) {
          const loadedSegments = (result.data.items ?? []).filter(
            (segment: SegmentItem) => segment.type === segmentType,
          );
          setSegments(loadedSegments);
          if (segmentType === "manual") {
            const eligibleIds = new Set(
              loadedSegments
                .filter(isManualSegmentEligible)
                .map((segment: SegmentItem) => segment.id),
            );
            onSelectionChange(
              selectedSegmentIds.filter((id) => eligibleIds.has(id)),
            );
          }
        }
      } catch (error) {
        console.error("Unable to load segments", error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
          onLoadingChange?.(false);
        }
      }
    };

    loadSegments();

    return () => {
      isMounted = false;
      onLoadingChange?.(false);
    };
  }, [onLoadingChange, segmentType]);

  const filteredSegments = useMemo(() => {
    const query = searchValue.trim().toLowerCase();

    if (!query) {
      return segments;
    }

    return segments.filter((segment) =>
      [segment.id, segment.title, segment.description, segment.type].some(
        (value) =>
          String(value ?? "")
            .toLowerCase()
            .includes(query),
      ),
    );
  }, [searchValue, segments]);

  const isRowSelectable = (segment: SegmentItem) => {
    if (segmentType === "auto") {
      return Boolean(segment.isActive);
    }
    return isManualSegmentEligible(segment);
  };

  const visibleIds = filteredSegments
    .filter(isRowSelectable)
    .map((segment) => segment.id)
    .filter((id): id is number => id !== undefined);
  const areAllVisibleSelected =
    visibleIds.length > 0 &&
    visibleIds.every((id) => selectedSegmentIds.includes(id));

  const toggleSegment = (id: number) => {
    const segment = segments.find((item) => item.id === id);
    const isSelected = selectedSegmentIds.includes(id);
    if (!segment) {
      return;
    }
    if (segmentType === "auto") {
      if (!segment.isActive && !isSelected) {
        return;
      }
    } else if (!isManualSegmentEligible(segment)) {
      return;
    }

    onSelectionChange(
      selectedSegmentIds.includes(id)
        ? selectedSegmentIds.filter((segmentId) => segmentId !== id)
        : [...selectedSegmentIds, id],
    );
  };

  const toggleAllVisible = () => {
    if (areAllVisibleSelected) {
      onSelectionChange(
        selectedSegmentIds.filter((id) => !visibleIds.includes(id)),
      );
      return;
    }

    onSelectionChange(
      Array.from(new Set([...selectedSegmentIds, ...visibleIds])),
    );
  };

  if (isLoading) {
    return <ModalTabSkeleton />;
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.toolbar}>
        <SearchInput
          placeholder={t("searchSegment")}
          value={searchValue}
          onChange={setSearchValue}
          isSmall
        />

        <span className={styles.selectionCount}>
          {t("segmentsSelected", { count: selectedSegmentIds.length })}
        </span>
      </div>

      <div className={styles.table}>
        <div className={`${styles.row} ${styles.header} ${showUsersColumn ? "" : styles.rowWithoutUsers}`}>
          <div className={styles.checkboxCell}>
            <Checkbox
              checked={areAllVisibleSelected}
              disabled={visibleIds.length === 0}
              onClick={toggleAllVisible}
            />
          </div>
          <div className={styles.id}>ID</div>
          <div className={styles.title}>{t("title").toUpperCase()}</div>
          <div className={styles.description}>
            {t("description").toUpperCase()}
          </div>
          <div className={styles.type}>{t("type").toUpperCase()}</div>
          <div className={styles.active}>{t("active").toUpperCase()}</div>
          {showUsersColumn && (
            <div className={styles.users}>{t("users").toUpperCase()}</div>
          )}
        </div>

        {filteredSegments.length === 0 ? (
          <div className={styles.empty}>{t("noSegmentsFoundShort")}</div>
        ) : (
          filteredSegments.map((segment) => {
            const id = segment.id ?? 0;
            const isSelected = selectedSegmentIds.includes(id);
            const canSelect = segmentType === "auto"
              ? Boolean(segment.isActive) || isSelected
              : isManualSegmentEligible(segment);

            return (
              <div
                className={`${styles.row} ${showUsersColumn ? "" : styles.rowWithoutUsers} ${
                  isSelected ? styles.selected : ""
                } ${!canSelect ? styles.disabledRow : ""}`}
                key={id}
                onClick={() => canSelect && toggleSegment(id)}
              >
                <div className={styles.checkboxCell}>
                  {canSelect ? (
                    <Checkbox
                      id={id}
                      checked={isSelected}
                      onClick={() => toggleSegment(id)}
                    />
                  ) : (
                    <span
                      className={styles.unavailableMark}
                      aria-label={t("segmentUnavailableForDispatch")}
                    >
                      ×
                    </span>
                  )}
                </div>
                <div className={styles.id}>{id}</div>
                <div className={styles.title}>{segment.title}</div>
                <div className={styles.description}>{segment.description}</div>
                <div className={styles.type}>{t(segment.type)}</div>
                <div className={styles.active}>
                  <ActiveLabel isActive={Boolean(segment.isActive)} />
                </div>
                {showUsersColumn && (
                  <div className={styles.users}>{segment.countOfUsers ?? 0}</div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
